import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
    MessageModel,
    ConversationContentProps,
} from '@chatscope/chat-ui-kit-react';
import { RawConversation, RawConversationOptionalId, RawMessage } from './chatSelectors';
import { AppDispatch } from '../app/store';
import { selectSessionRTK, selectIsAuthenticated } from './userTokenManagementSlice';

// --- Types for API CONTRACT ---

/** The structure of a raw message received from the server (e.g., during polling) */
// interface RawMessage {
//     id: string;
//     message: string;
//     sender: string;
//     direction: 'incoming' | 'outgoing';
//     sentTime: string;
//     timestamp: number; // Unix epoch in milliseconds
// }

// export interface RawMessage {
//     id: string;
//     text: string;
//     senderId: string;
//     date: number; // Unix timestamp
//     isRead: boolean;
// }

// export interface RawConversation {
//     id: string;
//     title: string;
//     lastMessageText: string;
//     lastMessageDate: number; // Unix timestamp
//     participants: { id: string; name: string }[];
//     // We assume the full list of messages is available in the conversation object
//     messages: RawMessage[]; 
// }

// /** The expected initial state response from the server */
// interface ChatState {
//     conversation_list: ConversationContentProps[];
//     messages_list: MessageModel[][];
//     /** The server-reported timestamp of the LATEST message known, used by the client to begin polling for *newer* messages. */
//     lastMessageTimestamp: number;
// }

/** The expected initial state response from the server */



export interface RawConversationOptionalIdMessagesTS
    extends RawConversation {//OptionalId{
    messages: RawMessage[];
    retrieved_at_ts: string
}
// --- RTK Query: API Definition ---

export type RowChatState = RawConversationOptionalIdMessagesTS[];

const API_BASE_URL = '/api/chat';

export const chatscopeApi = createApi({
    reducerPath: 'chatscopeApi',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE_URL,
        // Minimal auth injection (2026-08-02): FEDERATED sends the Cognito access token so the
        // map-server auth gate can verify it. CUSTOM/phone has no access_token in the FE session
        // (tokens live in httpOnly cookies, auto-sent same-origin) → no-op for custom. Identity is
        // still DERIVED server-side from the verified token; we never send a trusted `id` here.
        prepareHeaders: (headers, { getState }) => {
            const state = getState() as any;
            const session = selectSessionRTK(state);
            if (selectIsAuthenticated(state) && session?.access_token) {
                headers.set('Authorization', `Bearer ${session.access_token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['ChatState', 'Messages', 'InitMessages', 'Conversations', 'Unread'], // New tag names for clarity
    endpoints: (builder) => ({

        // Query: GET /api/v1/messages/since/{userId}?sinceDateTime=... (Polling)
        getConversations: builder.query<RawConversation[], { profileId: number, conversationId?: number }>({
            query: ({ profileId }) => ({
                // The polling endpoint now also includes the userId
                url: `conversations/`,
                params: { profileId: profileId },
            }),
            providesTags: ['Conversations']
        }),
        getMessages: builder.query<RawMessage[], { conversationId: number, viewerProfileId: number }>({
            query: ({ conversationId, viewerProfileId }) => ({
                // The polling endpoint now also includes the userId
                url: `messages/`,
                params: { conversationId: conversationId, viewerProfileId: viewerProfileId },
            }),
            providesTags: (result, error, { conversationId }) => [{ type: 'InitMessages', id: conversationId }]
        }),
        // GET unread messages count query
        getUnreadCount: builder.query<number, { profileId: number }>({
            query: ({ profileId }) => {
                return {
                    url: `unreadcount`,
                    params: { profileId },
                };
            },
            providesTags: ['Unread'],
        }),

        // RENAMED & MODIFIED: Query: GET /api/v1/state/{userId} (Initial State Load)
        getChatState: builder.query<RowChatState, { userId: number, lastRetrievedTS?: number, lastRetrievedConversationIds?: number[], }>({ // Takes string (userId) argument
            query: ({ lastRetrievedTS, userId, lastRetrievedConversationIds }) => ({
                method: 'POST',
                url: `state/`,
                params: { ...lastRetrievedTS ? { lastRetrievedTS: lastRetrievedTS } : {}, profileId: userId },
                body: {
                    lastRetrievedConversationIds: lastRetrievedConversationIds,
                },
            }),
            providesTags: ['ChatState']
        }),

        // Query: GET /api/v1/messages/since/{userId}?sinceDateTime=... (Polling)
        pollForNewMessages: builder.query<RawMessage[], { lastChecked: number; userId: number }>({
            query: ({ lastChecked, userId }) => ({
                // The polling endpoint now also includes the userId
                url: `new-messages`,
                params: { lastCheckTimeStamp: lastChecked, profileId: userId },
            }),
            //providesTags: ['Messages']
        }),

        // Mutation: POST /api/v1/addMessage
        addMessage: builder.mutation<{ message: RawMessage }, Omit<RawMessage, "id" | "sent_at" | "is_read"> & { conversation_id: number, makeMemberIfNot?: boolean }>({//{ conversationId: number; text: string; userId: number }>({
            query: (body) => ({
                url: 'messages',
                method: 'POST',
                body,
            }),
            // invalidatesTags: (result, error, {conversation_id}) => [
            //     { type: 'InitMessages' as const, id: conversation_id},
            // ],

        }),
        setReadConversation: builder.mutation<{ message: string }, { conversationId: number; userId: number }>({
            query: (body) => ({
                url: 'setReadConversation',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Unread', 'Conversations'],
        }),
        setReadMessage: builder.mutation<{ message: string }, { messageId: number; userId: number }>({
            query: (body) => ({
                url: 'setReadMessage',
                method: 'POST',
                body,
            }),
            // onQueryStarted(_, { queryFulfilled }) {
            //     // keep mutation alive, but don't await
            //     queryFulfilled.catch(() => {
            //         // ignore or log
            //     });
            // },
            //invalidatesTags: ['Unread', 'Conversations'],
        }),
        // Mutation: POST /api/v1/addConversation
        addConversation: builder.mutation<{ index: number }, { body: any }>({ // Requires userId//name?: string; userId: string }>({ // Requires userId
            query: (body) => ({
                url: 'addConversation',
                method: 'POST',
                body,
            }),
            // Invalidation triggers a refetch of getChatState (which uses userId)
            invalidatesTags: ['ChatState']
        })
    })
});


// Export the hooks
export const {
    useGetConversationsQuery,
    useGetUnreadCountQuery,
    useGetMessagesQuery,
    useGetChatStateQuery, // RENAMED HOOK
    useLazyGetChatStateQuery, // RENAMED HOOK
    useAddConversationMutation,
    useAddMessageMutation,
    useLazyPollForNewMessagesQuery,
    useSetReadConversationMutation

} = chatscopeApi;


// export const markMessageReadThunk =
//   (messageId: number, userId: number) =>
//   async (dispatch: AppDispatch) => {
//     try {
//       const result = await dispatch(
//         chatscopeApi.endpoints.setReadMessage.initiate({ messageId, userId })
//       ).unwrap(); // await keeps mutation alive
//       console.log('Backend response:', result);
//       return result;
//     } catch (err) {
//       console.error('Mutation failed:', err);
//     }
//   };